import { ShareTicketBody } from "@mg/schemas/src/christo/catalyst";
import { Flex, Grid, Heading, Link } from "@radix-ui/themes";
import { useQuery } from "@tanstack/react-query";
import {
  // eslint-disable-next-line import/named
  Link as NavLink,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import { usePostHog } from "posthog-js/react";
import { useState } from "react";

import { DeleteSingleWorkflowDialog } from "./components/DeleteWorkflowDialog";
import { workflowRoute } from "./route";

import Workflow from "../../components/workflow/Workflow";
import { useUI } from "../../contexts/ui";
import { getGuidelines } from "../../services/brand-guidelines";
import { getFolder } from "../../services/projects";
import { isNil } from "../../utils/fp";
import { downloadAsset } from "../../utils/http";
import {
  useDownloadFolderLink,
  useShareFolder,
  useShareFolderLink,
} from "../../utils/queries/folders";
import {
  useDeleteFolderMutation,
  useShareTicket,
  useShareTicketLink,
  useUpdateFolderMutation,
} from "../../utils/queries/projects";
import { queryClient } from "../../utils/queryClient";
import PunttDashboardLoader from "../index/loader";
import { indexRoute } from "../index/route";
import { Breadcrumbs } from "../tickets/components/Breadcrumbs";
import {
  ShareDialog,
  ShareDialogProps,
} from "../tickets/components/dialogs/ShareDialog";
import { RenameDialog } from "../tickets/components/UpdateNameDialog";

export default function PunttWorkflowView() {
  const { personas: requestedPersonas, folderId } = workflowRoute.useSearch();

  const [cachedFolderId] = useState(folderId);
  const router = useRouter();
  const navigate = useNavigate({ from: workflowRoute.to });

  const posthog = usePostHog();
  const { notify } = useUI();

  const deleteFolderMutation = useDeleteFolderMutation();
  const folderShareLinkMutation = useShareFolderLink();
  const shareFolderMutation = useShareFolder();
  const ticketShareLinkMutation = useShareTicketLink();
  const folderDownloadMutation = useDownloadFolderLink();
  const updateFolderMutation = useUpdateFolderMutation();
  const shareTicketMutation = useShareTicket();

  const [renamingId, setRenamingId] = useState<string>();
  const [deletingFolderId, setDeletingFolderId] = useState<string>();
  const [shareItem, setShareItem] = useState<ShareDialogProps["item"]>();
  const [shareLink, setShareLink] = useState<string>();

  const guidelinesQuery = useQuery({
    queryKey: ["brand-guidelines"],
    queryFn: getGuidelines,
  });
  const folderQuery = useQuery({
    queryKey: ["folders", cachedFolderId ?? folderId],
    queryFn: () => getFolder(cachedFolderId! ?? folderId!),
    enabled: !isNil(cachedFolderId ?? folderId),
  });

  function handleShareFolder(folderId: string, emails: string[]) {
    const startTime = performance.now();
    shareFolderMutation.mutate(
      { folderId, emails },
      {
        onSuccess() {
          const folder = folderQuery.data;

          posthog.capture("emailed_folder_share_link", {
            folder_id: folderId,
            folder_name: folder?.name,
            emails,
            num_shares: emails.length,
            duration: performance.now() - startTime,
          });

          setShareLink(undefined);
          setShareItem(undefined);
          notify({
            title: "Folder shared",
            message: `Email has been sent to ${emails.length} recipient(s)`,
          });
        },
      },
    );
  }

  function handleDownloadFolder(folderId: string) {
    const startTime = performance.now();
    folderDownloadMutation.mutate(folderId, {
      onSuccess(data) {
        const folder = folderQuery.data;

        posthog.capture("download_folder", {
          folder_id: folderId,
          folder_name: folder?.name,
          data_length: data?.url.length,
          request_duration: performance.now() - startTime,
        });
        if (data) {
          downloadAsset(data.url);
        }
      },
    });
  }
  const allPersonas = (guidelinesQuery.data?.aiPersonas ?? []).filter(
    (p) => p.enabled,
  );
  let personas = requestedPersonas.filter((p) =>
    allPersonas.some((ap) => ap.name === p),
  );
  if (!personas.length) personas = allPersonas.map((p) => p.name);

  if (!isNil(cachedFolderId) && folderQuery.isPending) {
    return <PunttDashboardLoader />;
  }

  return (
    <>
      {" "}
      <ShareDialog
        item={shareItem}
        latestVersionNumber={undefined}
        shareLink={shareLink}
        shareLinkPending={
          folderShareLinkMutation.isPending || ticketShareLinkMutation.isPending
        }
        isPending={
          shareFolderMutation.isPending || shareTicketMutation.isPending
        }
        onCancel={() => setShareItem(undefined)}
        onShare={(
          emails: string[],
          _shareVersion: ShareTicketBody["version"],
        ) => {
          if (shareItem == null) {
            throw new Error("No item to share");
          }

          switch (shareItem.type) {
            case "folder":
              return handleShareFolder(shareItem.id, emails);
            default:
              throw new Error("Unknown share item type");
          }
        }}
        onVersionChange={(version) =>
          shareTicketMutation.mutate(
            { ticketId: shareItem!.id, version },
            {
              onSuccess(data) {
                if (data == null) {
                  throw new Error("An unknown error occurred");
                }

                setShareLink(data.url);
              },
            },
          )
        }
      />
      <RenameDialog
        isPending={updateFolderMutation.isPending}
        _id={renamingId}
        onCancel={() => setRenamingId(undefined)}
        onSave={(_id, folderName) => {
          return updateFolderMutation.mutateAsync(
            { _id, name: folderName },
            {
              onSuccess() {
                setRenamingId(undefined);
                posthog.capture("folder_renamed", {
                  folder_id: _id,
                  folder_name: folderName,
                });
                return router.invalidate();
              },
            },
          );
        }}
        name={folderQuery.data?.name}
      />
      <DeleteSingleWorkflowDialog
        name={folderQuery.data?.name ?? ""}
        isPending={deleteFolderMutation.isPending}
        folderId={deletingFolderId}
        onCancel={() => setDeletingFolderId(undefined)}
        onDelete={() => {
          if (deletingFolderId == null) {
            throw new Error(
              "The folder we want to delete cannot have a null or undefined ID",
            );
          }

          deleteFolderMutation.mutate(deletingFolderId, {
            onSuccess() {
              if (folderId === deletingFolderId) {
                navigate({
                  to: indexRoute.to,
                });
              }
              setDeletingFolderId(undefined);
              posthog.capture("folder_deleted", {
                folder_id: deletingFolderId,
              });
              queryClient.invalidateQueries({ queryKey: ["folders", "tree"] });
              return router.invalidate();
            },
            onError() {
              window.alert("Unable to delete folder. Please try again.");
            },
          });
        }}
      />
      <Grid gap="4">
        <header className="flex items-center justify-between gap-4 p-4">
          <Flex align="center" gap="2">
            <Heading as="h1" size="5" weight="bold">
              <Link asChild data-auth-trigger="projects-link">
                <NavLink
                  to={indexRoute.to}
                  search={({ folderId: _folderId, ...prev }) => prev}
                  className="puntt-link"
                >
                  Workflows
                </NavLink>
              </Link>
            </Heading>

            {folderId && (
              <Breadcrumbs
                onDeleteFolder={() => setDeletingFolderId(folderId)}
                onShareFolder={() => {
                  setShareLink(undefined);
                  setShareItem({ id: folderId, type: "workflow" });
                  folderShareLinkMutation.mutate(folderId, {
                    onSuccess(data) {
                      if (data == null) {
                        throw new Error("An unknown error occurred");
                      }

                      setShareLink(data.url);
                    },
                  });
                }}
                onRenameFolder={() => setRenamingId(folderId)}
                onDownloadFolder={() => handleDownloadFolder(folderId)}
                ticketTitle={folderQuery.data?.name}
              />
            )}
          </Flex>
        </header>

        <Workflow
          folderId={folderId}
          // @ts-expect-error TS2322: folder can totally be null or undefined, we
          // don't care.
          folder={folderQuery.data}
          // @ts-expect-error TS2322: we don't make assumptions here about which personas can be used.
          // That happens further downstream. Also, if a persona can't be used, a review simply doesn't run.
          personas={requestedPersonas}
        />
      </Grid>
    </>
  );
}
